import React, { JSX } from 'react';
import { Box, Center, Flex, useRadio, useRadioGroup, UseRadioProps } from '@chakra-ui/react';

import { useTranslations } from '../../../contexts/LocalizationContext';

const RadioCardItem = (props: UseRadioProps & { children: React.ReactNode; index: number; total: number }) => {
  const { getInputProps, getCheckboxProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as="label" flex={1}>
      <input {...input} />
      <Box
        {...checkbox}
        cursor="pointer"
        height={'100%'}
        minH={'41px'}
        borderWidth={1}
        borderLeftRadius={props.index === 0 ? 10 : 0}
        borderLeftWidth={props.index === 0 ? '1px' : '0.5px'}
        borderRightWidth={props.index === props.total - 1 ? '1px' : '0.5px'}
        borderRightRadius={props.index === props.total - 1 ? 10 : 0}
        borderColor="grey.100"
        fontWeight="bold"
        color="grey.200"
        _checked={{
          bg: 'orange.500',
          color: 'grey.900',
          borderColor: 'grey.900',
          borderLeftWidth: '1px',
          borderRightWidth: '1px',
          // Overlap adjacent 0.5px borders so the junction shows a single 1px border
          ml: props.index !== 0 ? '-0.5px' : undefined,
          mr: props.index !== props.total - 1 ? '-0.5px' : undefined,
          position: 'relative',
          zIndex: 1,
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        p={1}
      >
        <Center h={'100%'}>{props.children}</Center>
      </Box>
    </Box>
  );
};

type Props = {
  options: string[];
  name: string;
  defaultValue: string;
  onChange: (value: never) => void;
  bgColor?: string;
  width?: string;
};

export const RadioCard = ({ options, name, onChange, defaultValue, bgColor, width }: Props): JSX.Element => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    onChange,
  });

  const translations = useTranslations();
  const group = getRootProps();

  return (
    <Flex direction={'row'} w={width ?? ['100%', '100%', '100%', '81%', '77%']} {...group} bgColor={bgColor}>
      {options.map((option, index) => {
        const radio = getRadioProps({ value: option });
        return (
          <RadioCardItem key={option} {...radio} index={index} total={options.length}>
            {translations(option)}
          </RadioCardItem>
        );
      })}
    </Flex>
  );
};
